import { RevisionRoles, RevisionType } from '../__generated__/graphql';

export const revisionRoleToDisplayText = (role: RevisionRoles) => {
  switch (role) {
    case RevisionRoles.ProjectLead:
      return 'PL';
    case RevisionRoles.InteralQualityAssurer:
      return 'IKS';
    case RevisionRoles.PeerReviewer:
      return 'SM';
    case RevisionRoles.Author:
      return 'FF';
    case RevisionRoles.ProfessionQualityAssurer:
      return 'FKS';
    case RevisionRoles.Dac:
      return 'DAK';
    case RevisionRoles.PublishingEditor:
      return 'RED';
    default:
      throw Error(`Unknown role: '${role}'`);
  }
};

export const revisionRolePeopleField = {
  [RevisionRoles.ProjectLead]: 'projectLeads',
  [RevisionRoles.InteralQualityAssurer]: 'internalQualityAssurers',
  [RevisionRoles.PeerReviewer]: 'peerReviewers',
  [RevisionRoles.Author]: 'authors',
  [RevisionRoles.ProfessionQualityAssurer]: 'professionQualityAssurers',
  [RevisionRoles.Dac]: 'dacs',
  [RevisionRoles.PublishingEditor]: 'publishingEditors',
} as const;

export type RevisionRolePeopleField = (typeof revisionRolePeopleField)[keyof typeof revisionRolePeopleField];

type WithRoleHolders = Partial<Record<RevisionRolePeopleField, readonly ({ name?: string | null } | null)[] | null>>;

export const revisionRolePeopleNames = (revision: WithRoleHolders | null | undefined, role: RevisionRoles) => {
  const field = revisionRolePeopleField[role as keyof typeof revisionRolePeopleField];
  return (field ? (revision?.[field] ?? []) : []).map((person) => person?.name).filter((name): name is string => !!name);
};

export const progressResponsibilityRoles = [
  RevisionRoles.ProjectLead,
  RevisionRoles.InteralQualityAssurer,
  RevisionRoles.PeerReviewer,
  RevisionRoles.Author,
  RevisionRoles.ProfessionQualityAssurer,
  RevisionRoles.Dac,
  RevisionRoles.PublishingEditor,
];

export const sortProgressResponsibility = (roles: readonly RevisionRoles[] | null | undefined) =>
  progressResponsibilityRoles.filter((role) => roles?.includes(role));

export const revisionTypeToShortText = (type: RevisionType | null | undefined) => {
  switch (type) {
    case RevisionType.Revision:
      return 'Fullstendig';
    case RevisionType.Simple:
      return 'Begrenset';
    case RevisionType.Withdrawal:
      return 'Tilbaketrekning';
    case RevisionType.Correction:
      return 'Feilretting';
    default:
      return null;
  }
};

export const revisionTypeShortTexts = ['Fullstendig', 'Begrenset', 'Tilbaketrekning', 'Feilretting'];

export const revisionTypeToDisplayText = (type: RevisionType | null | undefined) => {
  if (!type) return null;
  switch (type) {
    case RevisionType.Correction:
      return 'Feilretting (utgår)';
    case RevisionType.Revision:
      return 'Fullstendig revisjon';
    case RevisionType.Simple:
      return 'Begrenset revisjon';
    case RevisionType.Withdrawal:
      return 'Tilbaketrekke';
    default:
      throw Error(`Unknown type: '${type}'`);
  }
};

export const getRevisionStatus = ({
  completedAt,
  canceledAt,
}: {
  completedAt?: string | null | undefined;
  canceledAt?: string | null | undefined;
}) => {
  if (completedAt) {
    return 'Ferdig';
  }

  if (canceledAt) {
    return 'Avbrutt';
  }

  return 'Opprettet';
};
